import {
  ReadonlyWatchable,
  TargetableModel,
  useActionsContext,
  useWatchableValue,
  Watchable,
} from '../../actions/framework';
import { ChangeIndicator } from './change-indicator';
import {
  Tab,
  useActiveTab,
  useTabParams,
  useTabManager,
} from '../../tab-manager';
import { useEffect, useMemo } from 'react';
import { TabItem } from './tab-item';

const noopWatchable = Watchable.fromValue(undefined);

export const TabIndicator: React.FC<{
  model: {
    changes: ReadonlyWatchable<Record<string, boolean | string>>;
    route?: ReadonlyWatchable<string>;
    pageTitle: ReadonlyWatchable<string>;
  } & TargetableModel;
  onClose?: (tabId: string) => boolean;
}> = ({ model, onClose }) => {
  const { tabId } = useTabParams<{ tabId: string }>();
  const route = useWatchableValue(model.route ?? noopWatchable);

  const params: Record<string, string> =
    route === undefined
      ? {}
      : {
          route,
        };

  return (
    <Tab tabId={tabId} params={params}>
      <TabView model={model} tabId={tabId} onClose={onClose} />
    </Tab>
  );
};

const TabView: React.FC<{
  model: {
    changes: ReadonlyWatchable<Record<string, boolean | string>>;
    pageTitle: ReadonlyWatchable<string>;
  } & TargetableModel;
  tabId: string;
  onClose?: (tabId: string) => boolean;
}> = ({ model, tabId, onClose }) => {
  const tabTitle = useWatchableValue(model.pageTitle);
  const activeTab = useActiveTab();
  const actionsContext = useActionsContext();
  const { renderedTabs } = useTabManager();

  useEffect(() => {
    if (activeTab === tabId) {
      actionsContext.targetModel(model);
    } else {
      actionsContext.markAsNotTargeted(model);
    }
  }, [tabId, activeTab, actionsContext, model]);

  const renderedTab = renderedTabs[tabId];

  // Optimized fallback icon logic using memoization and early returns
  const getFallbackIcon = useMemo(() => {
    const route = tabId.toLowerCase();
    const titleLower = tabTitle.toLowerCase();

    // Use startsWith for faster matching when possible, includes for partial matches
    if (route.startsWith('/identities') || titleLower === 'identities') {
      return 'finger-print';
    }
    if (route.startsWith('/runs') || titleLower.includes('monitoring')) {
      return 'activity';
    }
    if (
      route.startsWith('/operations') ||
      (titleLower.includes('operations') && !titleLower.includes('monitoring'))
    ) {
      return 'document-magnifying-glass';
    }
    if (route.startsWith('/repl')) {
      return 'window';
    }
    if (route.startsWith('/definition')) {
      return 'variable';
    }
    if (route.startsWith('/dataset')) {
      return 'table';
    }

    return undefined;
  }, [tabId, tabTitle]);

  return (
    <TabItem
      tab={{
        id: tabId,
        title: tabTitle,
        displayTitle: tabTitle ?? renderedTab?.displayTitle,
        icon: renderedTab?.icon ?? getFallbackIcon,
        tabView: (
          <ChangeIndicator model={model} tabId={tabId} onClose={onClose} />
        ),
        showClose: false,
        onClose,
      }}
    />
  );
};
